import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { Observable } from 'rxjs';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import { platformIsMac } from '@iris/common/utils/dom.utils';

export class IrisSelectionService<T extends { id: string }> {
  private readonly _selection: SelectionModel<T> = new SelectionModel<T>(true, []);

  public selectionChange$: Observable<SelectionChange<T>> = this.selection.changed.pipe(
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
  public selectionChanged$: Observable<T[]> = this.selectionChange$.pipe(
    debounceTime(300),
    map(_ => {
      return this.selection.selected;
    }),
  );

  get selection(): SelectionModel<T> {
    return this._selection;
  }

  get hasValue(): boolean {
    return this._selection.hasValue();
  }

  get length(): number {
    return this._selection.selected.length;
  }

  clear(): IrisSelectionService<T> {
    this._selection.clear();
    return this;
  }

  getSelectedItemById(itemId: string): T {
    return this._selection.selected.find(({ id }) => id === itemId);
  }

  isItemSelected(itemId: string): boolean {
    return !!this.getSelectedItemById(itemId);
  }

  selectIfNotSelected(item: T, unselectOthers = false): IrisSelectionService<T> {
    if (!this.isItemSelected(item.id)) {
      if (unselectOthers) {
        this._selection.clear();
      }
      this._selection.select(item);
    }
    return this;
  }

  deselect(item: T): IrisSelectionService<T> {
    if (this.isItemSelected(item.id)) {
      const selectedItem = this.getSelectedItemById(item.id);
      this._selection.deselect(selectedItem);
    }
    return this;
  }

  toggleSelection(item: T): IrisSelectionService<T> {
    const selectedItem = this.getSelectedItemById(item.id);
    if (selectedItem) {
      this.deselect(selectedItem);
    } else {
      this.selectIfNotSelected(item);
    }
    return this;
  }

  changeSelection(item: T, select: boolean, unselectOthers = false): IrisSelectionService<T> {
    if (select) {
      this.selectIfNotSelected(item, unselectOthers);
    } else {
      this.deselect(item);
    }
    return this;
  }

  setSelection(event: MouseEvent, item: T, items: T[]): IrisSelectionService<T> {
    const isMac = platformIsMac();

    if (event.metaKey && isMac || event.ctrlKey && !isMac) {
      this.toggleSelection(item);
    } else if (event.shiftKey) {
      const lastSelectedRow = this._selection.selected[this._selection.selected.length - 1];
      const startIndex = items.findIndex(({ id }) => lastSelectedRow.id === id);
      const endIndex = items.findIndex(({ id }) => item.id === id);
      if (endIndex > startIndex) {
        this.selection.select(
          ...items
            .slice(startIndex, endIndex + 1)
            .filter(({ id }) => !this.isItemSelected(id)));
      } else {
        this.selection.select(
          ...items.slice(endIndex, startIndex)
            .filter(({ id }) => !this.isItemSelected(id))
            .reverse());
      }
    } else {
      this._selection.clear();
      this.changeSelection(item, true);
    }

    return this;
  }
}
